// Epoch 21: Scalable Conversation Parsing System
// Admin UI for Outreach Team Management
// Allows admins to configure team members for conversation parsing

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { useAuthSession } from '../../hooks/useAuthSession';

interface OutreachTeamMember {
  id: string;
  team_member_name: string;
  is_active: boolean;
  role_description?: string;
  contact_email?: string;
  team_lead_id?: string;
  linkedin_url?: string;
  created_at: string;
  updated_at: string;
  aliases: OutreachTeamAlias[];
}

interface OutreachTeamAlias {
  id: string;
  team_config_id: string;
  alias_name: string;
  confidence_weight: number;
  is_primary: boolean;
  created_at: string;
}

interface TeamMemberFormData {
  team_member_name: string;
  role_description: string;
  contact_email: string;
  linkedin_url: string;
  is_active: boolean;
  aliases: string[];
}

const OutreachTeamManagement: React.FC = () => {
  const { session } = useAuthSession();
  const [teamMembers, setTeamMembers] = useState<OutreachTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<OutreachTeamMember | null>(null);
  const [formData, setFormData] = useState<TeamMemberFormData>({
    team_member_name: '',
    role_description: '',
    contact_email: '',
    linkedin_url: '',
    is_active: true,
    aliases: []
  });
  const [processing, setProcessing] = useState(false);

  // Check if user is admin
  const isAdmin = session?.user?.user_metadata?.role === 'admin' || 
                  session?.user?.user_metadata?.role === 'super_admin';

  const loadTeamMembers = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('outreach-team-config', {
        body: { action: 'list' }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setTeamMembers(data.data || []);
    } catch (err) {
      console.error('Error loading team members:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  const resetForm = () => {
    setFormData({
      team_member_name: '',
      role_description: '',
      contact_email: '',
      linkedin_url: '',
      is_active: true,
      aliases: []
    });
    setEditingMember(null);
    setShowAddForm(false);
  };

  const handleEdit = (member: OutreachTeamMember) => {
    setEditingMember(member);
    setFormData({
      team_member_name: member.team_member_name,
      role_description: member.role_description || '',
      contact_email: member.contact_email || '',
      linkedin_url: member.linkedin_url || '',
      is_active: member.is_active,
      aliases: member.aliases
        .filter(alias => !alias.is_primary)
        .map(alias => alias.alias_name)
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.team_member_name.trim()) return;

    setProcessing(true);
    setError(null);

    try {
      const teamMemberData = {
        team_member_name: formData.team_member_name.trim(),
        role_description: formData.role_description.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        linkedin_url: formData.linkedin_url.trim() || null,
        is_active: formData.is_active,
        aliases: formData.aliases
          .filter(alias => alias.trim())
          .map(alias => ({
            alias_name: alias.trim(),
            confidence_weight: 0.8,
            is_primary: false
          }))
      };

      const action = editingMember ? 'update' : 'create';
      const requestBody = editingMember 
        ? { action, id: editingMember.id, data: teamMemberData }
        : { action, data: teamMemberData };

      const { data, error } = await supabase.functions.invoke('outreach-team-config', {
        body: requestBody
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await loadTeamMembers();
      resetForm();
    } catch (err) {
      console.error('Error saving team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to save team member');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeactivate = async (member: OutreachTeamMember) => {
    if (!confirm(`Are you sure you want to deactivate ${member.team_member_name}?`)) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('outreach-team-config', {
        body: { 
          action: 'delete', 
          id: member.id 
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await loadTeamMembers();
    } catch (err) {
      console.error('Error deactivating team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate team member');
    } finally {
      setProcessing(false);
    }
  };

  const handleAliasChange = (index: number, value: string) => {
    const newAliases = [...formData.aliases];
    newAliases[index] = value;
    setFormData(prev => ({ ...prev, aliases: newAliases }));
  };

  const addAlias = () => {
    setFormData(prev => ({ ...prev, aliases: [...prev.aliases, ''] }));
  };

  const removeAlias = (index: number) => {
    setFormData(prev => ({
      ...prev,
      aliases: prev.aliases.filter((_, i) => i !== index)
    }));
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Restricted</h2>
          <p className="text-red-600">Admin access required to manage outreach team configuration.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Outreach Team Management</h1>
            <p className="text-gray-600 mt-2">
              Configure team members for intelligent conversation parsing
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Team Member
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">
              {teamMembers.filter(m => m.is_active).length}
            </div>
            <div className="text-sm text-gray-600">Active Members</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">
              {teamMembers.reduce((acc, m) => acc + m.aliases.length, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Aliases</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-purple-600">
              {teamMembers.filter(m => !m.is_active).length}
            </div>
            <div className="text-sm text-gray-600">Inactive Members</div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Member Name *
                </label>
                <input
                  type="text"
                  value={formData.team_member_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, team_member_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Phoebe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role Description
                </label>
                <input
                  type="text"
                  value={formData.role_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, role_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Senior Outreach Specialist"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., phoebe@standingencore.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile URL
                </label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., https://www.linkedin.com/in/phoebe-smith"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active member
                </label>
              </div>
            </div>

            {/* Aliases Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Aliases (Alternative Names)
                </label>
                <button
                  type="button"
                  onClick={addAlias}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Alias
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Add variations of the name that might appear in conversations (e.g., "Phoebe Smith", "P. Smith")
              </p>
              
              {formData.aliases.map((alias, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={alias}
                    onChange={(e) => handleAliasChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Phoebe Smith"
                  />
                  <button
                    type="button"
                    onClick={() => removeAlias(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing || !formData.team_member_name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Saving...' : editingMember ? 'Update Member' : 'Add Member'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
      <div className="space-y-4">
        {teamMembers.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No team members configured</h3>
            <p className="text-gray-500 mb-4">
              Add team members to enable intelligent conversation parsing.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Team Member
            </button>
          </div>
        ) : (
          teamMembers.map(member => (
            <div key={member.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {member.team_member_name}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      member.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {member.role_description && (
                    <p className="text-sm text-gray-600 mb-2">{member.role_description}</p>
                  )}
                  
                  {member.contact_email && (
                    <p className="text-sm text-gray-500 mb-1">{member.contact_email}</p>
                  )}

                  {member.linkedin_url && (
                    <a
                      href={member.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline mb-3 inline-block"
                    >
                      LinkedIn Profile →
                    </a>
                  )}

                  {/* Aliases */}
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Aliases:</h4>
                    <div className="flex flex-wrap gap-1">
                      {member.aliases.map(alias => (
                        <span 
                          key={alias.id}
                          className={`px-2 py-1 text-xs rounded ${
                            alias.is_primary 
                              ? 'bg-blue-100 text-blue-800 font-medium' 
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {alias.alias_name}
                          {alias.is_primary && ' (Primary)'}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="text-xs text-gray-400">
                    Created: {new Date(member.created_at).toLocaleDateString()}
                    {member.updated_at !== member.created_at && (
                      <> • Updated: {new Date(member.updated_at).toLocaleDateString()}</>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(member)}
                    disabled={processing}
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-50"
                  >
                    Edit
                  </button>
                  {member.is_active && (
                    <button
                      onClick={() => handleDeactivate(member)}
                      disabled={processing}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OutreachTeamManagement;